import { registerPromptFor, type QuestionId } from "./question-register.v1";
import {
  CHECKIN_SYMPTOM_FIELD_PART_KEYS,
  checkinSymptomFieldKey,
  SYMPTOM_REGISTER_V2,
  type CheckinFieldPrefix,
  type CheckinSymptomFieldPart,
} from "./symptom-entry";
import {
  TRANSFER_EARLIER_SYMPTOM_TYPES_KEY,
  TRANSFER_SYMPTOM_FIELD_PART_KEYS,
  TRANSFER_SYMPTOM_TYPES_KEY,
  transferEarlierSymptomFieldKey,
  transferSymptomFieldKey,
  type TransferEarlierSymptomFieldPart,
} from "./transfer-symptom-fields";

// TES-1050 (Emelie, Ö-278 b) — the deterministic journal text used while the
// AI support is off, and the text every AI section summary starts from.
//
//   "Journalfälten får inte fyllas med långa råa rader av typen ”Fråga? Svar”."
//   "använda versionssatta fältmappningar och fasta formuleringar"
//   "ta med medicinskt relevanta nekanden när mappningen kräver det"
//   "aldrig tolka, diagnostisera, formulera normalfynd eller skapa kliniska slutsatser"
//   "visa exakta frågor och originalsvar endast bakom Källa"
//
// So a form answer reaches a journal box only through an entry here: a short
// label taken from the form's own wording, then her answer exactly as the
// form renders it ("{label}: {value}"). The label is phrasing, never a
// clinical statement; where the form has no noun for a question, the label
// keeps the question's own words without the question mark. Which HEADING an
// answer belongs under is JOURNAL_PREFILL_MAP's (meeting-journal.ts); this
// map only decides whether it is written in the box, and how.
//
// A field with no entry here, a field from a template version before
// `fromVersion`, and a plain "no" on a field without `keepNegative` are not
// written in the box: they stay behind "Källa" under their heading
// (JournalPrefillLine.sourceOnly), with the exact question and her original
// answer. A new form field therefore never reaches the note's text until
// someone adds its entry.
//
// Versioning: bump JOURNAL_PHRASING_VERSION when an existing entry's wording
// changes. A field key that changes meaning in a new form version gets an
// entry with its own `fromVersion` (or a new key), never a silent rewording.

export const JOURNAL_PHRASING_VERSION = 2;

export interface JournalFieldPhrase {
  /** The label before her answer, from the form's own wording. */
  label: string;
  /**
   * A "no" on this field is a medically relevant negation and is written in
   * the box. Without it, a plain "no" stays behind "Källa" only.
   */
  keepNegative?: true;
  /**
   * A measured value: the unit is added when her answer is a bare number,
   * and the line always carries the day she gave it.
   */
  measure?: { unit: string };
  /**
   * A follow-up to another field of the same form. It joins its parent's
   * line ("{label}: {parent answer} – {this answer}") instead of a line of
   * its own; on its own (parent left out) it reads "{label}: {this answer}".
   */
  detailOf?: string;
  /**
   * The follow-up's answer stands in for the parent's (the parent only
   * opened it: "Jag vet ungefär när" → the date she gave).
   */
  replacesParent?: true;
  /**
   * TES-1251 — the same field key asked later in a register question's own
   * words (F-04 v7, F-05 v7), where earlier versions asked it otherwise.
   * When the question she was asked is that register copy, this label
   * replaces `label`. Matched on the wording of the version she answered,
   * never on its version number: version numbers differ per database.
   */
  asRegisterQuestion?: { questionId: QuestionId; label: string };
}

interface TemplatePhrasing {
  /** The first template version these field keys hold for. */
  fromVersion: number;
  fields: Readonly<Record<string, JournalFieldPhrase>>;
}

const NEG = { keepNegative: true } as const;

// TES-1251 — F-04 v7 and F-05 v7 ask the symptom questions once per symptom
// (symptom-entry.ts checkinSymptomFieldKey). The label names the symptom by
// its register name and the question in a few of its own words.
export const PER_SYMPTOM_LABEL: Readonly<
  Record<CheckinSymptomFieldPart, string>
> = {
  severity: "besvär just nu",
  trend: "förändring",
  started: "började eller förändrades",
  intake: "kunnat äta och dricka tillräckligt",
  note: "beskrivning",
};

function perSymptomPhrasing(
  prefix: CheckinFieldPrefix,
): Record<string, JournalFieldPhrase> {
  return Object.fromEntries(
    SYMPTOM_REGISTER_V2.flatMap((s) =>
      CHECKIN_SYMPTOM_FIELD_PART_KEYS.map((part) => [
        checkinSymptomFieldKey(prefix, s.code, part),
        { label: `${s.label.sv} – ${PER_SYMPTOM_LABEL[part]}` },
      ]),
    ),
  );
}

// F-02/F-02B (`intake`): the field keys are stable since v5 (meeting-journal.ts).
const INTAKE: TemplatePhrasing = {
  fromVersion: 5,
  fields: {
    med_current_weight: { label: "Vikt", measure: { unit: "kg" } },
    med_height: { label: "Längd", measure: { unit: "cm" } },
    med_recent_weight_change: {
      label: "Oväntad viktförändring de senaste 3 månaderna",
      ...NEG,
    },
    med_recent_weight_change_detail: {
      label: "Oväntad viktförändring de senaste 3 månaderna",
      detailOf: "med_recent_weight_change",
    },
    med_has_goal_weight: { label: "Målvikt eller målzon" },
    med_goal_weight: { label: "Målvikt", measure: { unit: "kg" } },
    // Intake v10–v15 only; v16 removed the question (TES-1162).
    med_goal_already_reached: { label: "Målvikt eller målzon redan nådd" },
    // TES-1162 (intake v16, F-02B only): patient-reported, never a phase.
    med_weight_within_goal: {
      label: "Nuvarande vikt inom målvikt eller målzon (patientuppgift)",
      keepNegative: true,
    },
    med_conditions: { label: "Nuvarande eller tidigare tillstånd", ...NEG },
    // "Typ 2-diabetes: gäller det här dig just nu?" → "Typ 2-diabetes: Ja, just nu"
    med_condition_status_typ2_diabetes: { label: "Typ 2-diabetes" },
    med_condition_status_prediabetes: { label: "Prediabetes" },
    med_condition_status_hogt_blodtryck: { label: "Högt blodtryck" },
    med_condition_status_blodfetter: { label: "Förhöjda blodfetter" },
    med_condition_status_hjart_karl: { label: "Hjärt-kärlsjukdom" },
    med_condition_status_stroke_tia: { label: "Stroke/TIA" },
    med_condition_status_somnapne: { label: "Obstruktiv sömnapné" },
    med_condition_status_pcos: { label: "PMOS" },
    med_condition_status_fettlever: { label: "Fettlever/leversjukdom" },
    med_condition_status_ledsjukdom: {
      label: "Ledsjukdom eller belastningssmärta",
    },
    med_condition_status_gikt: { label: "Gikt" },
    med_conditions_other: { label: "Annat tillstånd" },
    med_condition_treatment: {
      label: "Behandling för något av tillstånden idag",
      ...NEG,
    },
    med_condition_treatment_detail: {
      label: "Behandling för något av tillstånden idag",
      detailOf: "med_condition_treatment",
    },
    med_diabetes_treatment: { label: "Diabetesbehandling" },
    med_pancreatitis: { label: "Inflammation i bukspottkörteln", ...NEG },
    med_pancreatitis_detail: {
      label: "Inflammation i bukspottkörteln",
      detailOf: "med_pancreatitis",
    },
    med_gallbladder: {
      label: "Gallsten, gallblåseinflammation eller opererad gallblåsa",
      ...NEG,
    },
    med_kidney: {
      label:
        "Njursjukdom, nedsatt njurfunktion, dialys eller återkommande njursten",
      ...NEG,
    },
    med_liver: { label: "Annan leversjukdom än fettlever", ...NEG },
    med_gi: {
      label:
        "Svår eller långvarig mag-tarmsjukdom, gastropares eller återkommande svåra magsymtom",
      ...NEG,
    },
    med_eye_diabetes: {
      label: "Diabetesrelaterad ögonsjukdom eller pågående ögonbehandling",
      ...NEG,
    },
    med_surgery: {
      label: "Obesitaskirurgi eller annan större mag-tarmoperation",
      ...NEG,
    },
    med_surgery_detail: {
      label: "Obesitaskirurgi eller annan större mag-tarmoperation",
      detailOf: "med_surgery",
    },
    med_cancer_endocrine: {
      label:
        "Medullär sköldkörtelcancer, MEN2 eller annan relevant hormonproducerande tumör (själv eller nära biologisk familj)",
      ...NEG,
    },
    // TES-1164 — intake v17, asked only when her F-01 did not ask it.
    med_type1_diabetes: { label: "Diabetes typ 1", ...NEG },
    med_allergies: {
      label:
        "Kända läkemedelsallergier eller allvarliga överkänslighetsreaktioner",
      ...NEG,
    },
    med_allergies_detail: {
      label:
        "Kända läkemedelsallergier eller allvarliga överkänslighetsreaktioner",
      detailOf: "med_allergies",
    },
    med_pregnancy_status: {
      label: "Graviditet, amning och fertilitetsplan",
      ...NEG,
    },
    med_eating_disorder: {
      label: "Diagnos eller behandling för ätstörning, nu eller tidigare",
      ...NEG,
    },
    med_eating_disorder_detail: {
      label: "Diagnos eller behandling för ätstörning, nu eller tidigare",
      detailOf: "med_eating_disorder",
    },
    med_current_disordered_eating: {
      label:
        "Återkommande episoder av hetsätning, kräkning, svält/restriktion eller annan svårkontrollerad situation kring mat just nu",
      ...NEG,
    },
    med_mental_health: {
      label:
        "Pågående eller tidigare allvarlig depression, bipolär sjukdom, psykos eller annan psykisk ohälsa",
      ...NEG,
    },
    med_self_harm_safety: {
      label: "Tankar på att skada sig själv eller att inte vilja leva just nu",
      ...NEG,
    },
    med_takes_medications: {
      label: "Läkemedel eller kosttillskott just nu",
      ...NEG,
    },
    med_current_medications: {
      label: "Läkemedel eller kosttillskott just nu",
      detailOf: "med_takes_medications",
    },
    med_weight_medicine_history: {
      label: "Tidigare eller nuvarande läkemedel för vikt eller diabetes",
      ...NEG,
    },
    med_weight_medicine_history_detail: {
      label: "Tidigare eller nuvarande läkemedel för vikt eller diabetes",
      detailOf: "med_weight_medicine_history",
    },
    med_other_treatment: {
      label: "Pågående behandling hos annan vårdgivare",
      ...NEG,
    },
    med_other_treatment_detail: {
      label: "Pågående behandling hos annan vårdgivare",
      detailOf: "med_other_treatment",
    },
    med_current_symptoms: {
      label: "Aktuella symtom eller besvär",
      ...NEG,
    },
    med_current_symptoms_detail: {
      label: "Aktuella symtom eller besvär",
      detailOf: "med_current_symptoms",
    },
    // TES-1441 (intake v18) — the symptom list and the per-symptom questions
    // under yes/unsure, labelled like F-04/F-05's.
    med_current_symptom_types: { label: "Aktuella besvär" },
    ...perSymptomPhrasing("med"),
    med_recent_acute_care: {
      label:
        "Akutbesök, sjukhusvård eller ny allvarlig diagnos de senaste 12 månaderna",
      ...NEG,
    },
    med_recent_acute_care_detail: {
      label:
        "Akutbesök, sjukhusvård eller ny allvarlig diagnos de senaste 12 månaderna",
      detailOf: "med_recent_acute_care",
    },
    med_upcoming_procedure: {
      label: "Planerad operation, narkos eller större undersökning",
      ...NEG,
    },
    med_upcoming_procedure_detail: {
      label: "Planerad operation, narkos eller större undersökning",
      detailOf: "med_upcoming_procedure",
    },
    med_recent_labs: { label: "Tidigare blodprov", ...NEG },
    med_recent_labs_detail: {
      label: "Tidigare blodprov",
      detailOf: "med_recent_labs",
    },
    // TES-1125 (intake v14) — the structured follow-ups that replace the
    // free text above. Labels from the form's own wording, like F-07's
    // `pt_prior_labs_*` entries.
    // TES-1163 — its own line: from intake v16 the "no" option reads a bare
    // "Nej", which joined to "Tidigare blodprov: Ja" would read as no blood
    // test, and would be dropped as a plain negative without keepNegative.
    med_recent_labs_results: {
      label: "Tillgång till provsvaret",
      keepNegative: true,
    },
    med_recent_labs_sampled_on: { label: "Provdatum" },
    med_recent_labs_provider: { label: "Vårdgivare" },
    med_recent_labs_upload: { label: "Tidigare provsvar" },
    med_main_question: { label: "Vill framför allt få hjälp med" },
    med_anything_else: { label: "Övrigt" },
  },
};

// TES-1442 — F-07 v14 asks, per symptom, when it began and her note
// (transfer-symptom-fields.ts), labelled as the check-ins label them.
function transferSymptomPhrasing(): Record<string, JournalFieldPhrase> {
  return Object.fromEntries(
    SYMPTOM_REGISTER_V2.flatMap((s) =>
      TRANSFER_SYMPTOM_FIELD_PART_KEYS.map((part) => [
        transferSymptomFieldKey(s.code, part),
        { label: `${s.label.sv} – ${PER_SYMPTOM_LABEL[part]}` },
      ]),
    ),
  );
}

// TES-1532 — F-07 v17 asks, per symptom, when it began, whether she still
// has it and, on "Ja", how much it bothers her now. The labels keep the
// questions' own words ("När började besväret?", "Har du fortfarande det
// här besväret?"); the severity's is the check-ins' own.
const TRANSFER_EARLIER_PART_LABEL: Readonly<
  Record<Exclude<TransferEarlierSymptomFieldPart, "note">, string>
> = {
  onset: "började",
  still: "har fortfarande besväret",
  severity: PER_SYMPTOM_LABEL.severity,
};

function transferEarlierSymptomPhrasing(): Record<string, JournalFieldPhrase> {
  return Object.fromEntries(
    SYMPTOM_REGISTER_V2.flatMap((s) =>
      (["onset", "still", "severity"] as const).map((part) => [
        transferEarlierSymptomFieldKey(s.code, part),
        {
          label: `${s.label.sv} – ${TRANSFER_EARLIER_PART_LABEL[part]}`,
          // Her "Nej" says the symptom is over: a relevant negation.
          ...(part === "still" ? NEG : {}),
        },
      ]),
    ),
  );
}

// F-07 (`prescription_transfer`): stable since v1. In the transfer review's
// note the medicine, strength, dose and frequency are the medication
// paragraph instead (TRANSFER_PARAGRAPH_KEYS); the entries below are what the
// care meeting's note writes.
const TRANSFER: TemplatePhrasing = {
  fromVersion: 1,
  fields: {
    pt_medication: { label: "Läkemedel" },
    pt_medication_other: {
      label: "Läkemedel",
      detailOf: "pt_medication",
      replacesParent: true,
    },
    pt_strength: { label: "Styrka" },
    pt_dose: { label: "Dos vid varje doseringstillfälle" },
    pt_frequency: { label: "Doseringsfrekvens" },
    pt_frequency_other: {
      label: "Doseringsfrekvens",
      detailOf: "pt_frequency",
      replacesParent: true,
    },
    pt_treatment_start: { label: "Nuvarande behandlingsperiod började" },
    pt_treatment_started_at: {
      label: "Nuvarande behandlingsperiod började",
      detailOf: "pt_treatment_start",
      replacesParent: true,
    },
    pt_twelve_months_total: {
      label: "Behandling i minst tolv månader sammanlagt",
      ...NEG,
    },
    // TES-1099 — the single pause question (v1-v9 and v12 onwards).
    pt_break: { label: "Uppehåll i behandlingen", ...NEG },
    pt_break_details: {
      label: "Uppehåll i behandlingen",
      detailOf: "pt_break",
    },
    pt_long_break: {
      label: "Längre uppehåll under behandlingsperioden",
      ...NEG,
    },
    pt_long_break_details: {
      label: "Längre uppehåll under behandlingsperioden",
      detailOf: "pt_long_break",
    },
    pt_valid_prescription: {
      label: "Giltigt recept eller pågående ordination just nu",
      ...NEG,
    },
    pt_prescription_valid_until_known: { label: "Receptet gäller till" },
    pt_prescription_valid_until: {
      label: "Receptet gäller till",
      detailOf: "pt_prescription_valid_until_known",
      replacesParent: true,
    },
    pt_self_dose_change: {
      label: "Ändrat dosen utan att först komma överens med en läkare",
      ...NEG,
    },
    pt_self_dose_change_details: {
      label: "Ändrat dosen utan att först komma överens med en läkare",
      detailOf: "pt_self_dose_change",
    },
    pt_side_effects: {
      label:
        "Biverkningar eller andra besvär som kan hänga ihop med läkemedlet",
      ...NEG,
    },
    pt_side_effects_details: {
      label:
        "Biverkningar eller andra besvär som kan hänga ihop med läkemedlet",
      detailOf: "pt_side_effects",
    },
    // TES-1442 — F-07 v14's structured details (Ö-786 assumption (a)).
    [TRANSFER_SYMPTOM_TYPES_KEY]: {
      label: "Besvär som kan hänga ihop med läkemedlet",
    },
    ...transferSymptomPhrasing(),
    // TES-1532 — F-07 v17's details (Ö-786, Ö-813). The note keeps v14's
    // key and label above.
    [TRANSFER_EARLIER_SYMPTOM_TYPES_KEY]: {
      label: "Besvär under tidigare behandling",
    },
    ...transferEarlierSymptomPhrasing(),
    pt_prescription_upload: { label: "Receptunderlag" },
    pt_prescriber: { label: "Senaste förskrivare" },
    pt_remaining_supply: {
      label: "Läkemedel kvar med nuvarande dosering",
    },
    // Ö-289: "Provdatum och provkälla".
    pt_prior_labs_upload: { label: "Tidigare provsvar" },
    pt_prior_labs_date: { label: "Provdatum" },
    pt_prior_labs_source: { label: "Provkälla" },
    // Her F-07 reading, written only when no standing reading replaces it
    // (TES-1056).
    bp_systolic: { label: "Övertryck (systoliskt)" },
    bp_diastolic: { label: "Undertryck (diastoliskt)" },
    resting_pulse: { label: "Vilopuls" },
    bp_measured_on: { label: "Mätdatum" },
  },
};

// F-01 (`f01`, the BMI quiz): keys from both quiz generations.
const F01: TemplatePhrasing = {
  fromVersion: 1,
  fields: {
    weightKg: { label: "Vikt", measure: { unit: "kg" } },
    heightCm: { label: "Längd", measure: { unit: "cm" } },
    reproductiveStatus: {
      label: "Graviditet, amning och fertilitetsplan",
      ...NEG,
    },
    // TES-1164 (F-01 v10) — the two safety questions F-02 no longer asks.
    // A "Nej" is a medically relevant negation, as in F-02.
    type1Diabetes: { label: "Diabetes typ 1", ...NEG },
    mtcMen2: {
      label:
        "Medullär sköldkörtelcancer (MTC) eller MEN 2 hos patienten eller biologisk förälder, syskon eller barn",
      ...NEG,
    },
    impactDegree: { label: "Vikten påverkar hälsa eller vardag" },
    desiredOutcome: { label: "Viktigast när det gäller vikten just nu" },
    desiredOutcomeOther: {
      label: "Viktigast när det gäller vikten just nu",
      detailOf: "desiredOutcome",
    },
    whyLoseWeight: { label: "Varför gå ner i vikt" },
    impactOnLife: { label: "Övervikten påverkar livet" },
    howLongTried: { label: "Vikten har varit en utmaning" },
    changeContext: {
      label: "Vikten förändrades eller blev svårare att påverka",
    },
    methodsTried: { label: "Tidigare provat" },
    weightMedHistory: { label: "Erfarenhet av receptbelagd viktbehandling" },
    knownMedications: { label: "Känner till följande läkemedel" },
    bodySignals: { label: "Hunger, mättnad och sug" },
    biggestObstacles: { label: "Gör förändring svårt" },
    hardestPart: { label: "Svårast i viktresan" },
    emotionalEating: {
      label:
        "Mat för att hantera stress, trötthet, känslor eller behov av paus",
    },
    lifeStage: { label: "Livsfas" },
    // TES-1658 — F-01 v13's three new questions.
    hormonalLifePhase: { label: "Mens- eller hormonell livsfas" },
    weightChangeLifePhase: {
      label: "Samband mellan viktförändringen och en livsfas",
    },
    weightDifficulties: { label: "Gör vikten svår att påverka i dag" },
    activityLevel: { label: "Aktivitet i vardagen" },
  },
};

// F-04 (`weekly_checkin`): DOCTOR and SHARED answers only (the map's
// `allow`); the coach's part never reaches these labels.
const WEEKLY: TemplatePhrasing = {
  fromVersion: 1,
  fields: {
    week_new_or_changed_symptoms: {
      label: "Nya eller förändrade besvär sedan förra veckan",
      ...NEG,
      // v7 asks SYMPTOM_CHANGE_GATE: "Har du haft något nytt eller pågående
      // besvär, eller har något besvär förändrats sedan din senaste
      // incheckning?"
      asRegisterQuestion: {
        questionId: "SYMPTOM_CHANGE_GATE",
        label:
          "Nya, pågående eller förändrade besvär sedan senaste incheckningen",
      },
    },
    week_symptom_types: { label: "Besvär" },
    week_symptom_timing: { label: "Började eller förändrades" },
    week_symptom_severity: { label: "Besvärande som mest" },
    week_symptom_impact: { label: "Kan äta och dricka" },
    week_urgent_screen: { label: "Just nu", ...NEG },
    week_drive_mood_signal: {
      label:
        "Förändring i driv, glädje, initiativ eller lust sedan förra incheckningen",
      ...NEG,
    },
    week_drive_what: { label: "Märker mest" },
    week_drive_since: { label: "Började" },
    week_drive_impact: { label: "Påverkar" },
    week_dose_adherence: {
      label: "Tagit behandlingen enligt ordinationen sedan förra incheckningen",
      ...NEG,
    },
    week_dose_adherence_what: { label: "Vad som hände" },
    week_weight: { label: "Vikt", measure: { unit: "kg" } },
    week_wellbeing: { label: "Mående överlag den senaste veckan" },
    week_focus: { label: "Fungerat bäst den här veckan" },
    // v1/v2
    sideEffects: { label: "Biverkningar den här veckan", ...NEG },
    sideEffectSeverity: { label: "Biverkningarnas besvärlighet" },
    // v7 (TES-1251)
    ...perSymptomPhrasing("week"),
  },
};

// F-05 (`milestone`): the answers the meeting's and the monthly note read.
const MILESTONE: TemplatePhrasing = {
  fromVersion: 1,
  fields: {
    // TES-1251 (Ö-444) — F-04's wellbeing question, asked in F-05 from
    // template version 5 on, in F-04's wording.
    week_wellbeing: { label: "Mående överlag den senaste veckan" },
    month_hunger: { label: "Hunger" },
    month_satiety: { label: "Blir lagom mätt" },
    month_cravings: { label: "Sug eller tankar på mat har påverkat" },
    month_daily_function: { label: "Vardagen med behandlingen" },
    month_drive_mood_change: {
      label:
        "Förändring i driv, glädje, initiativ eller lust sedan förra månadsavstämningen",
      ...NEG,
      // v7 asks DRIVE_MOOD_CHANGE, F-04's question: "…sedan förra
      // incheckningen?"
      asRegisterQuestion: {
        questionId: "DRIVE_MOOD_CHANGE",
        label:
          "Förändring i driv, glädje, initiativ eller lust sedan förra incheckningen",
      },
    },
    // TES-1453 (Ö-735) — her symptoms, written as one paragraph per F-05
    // (f05SymptomSentences in meeting-journal.ts), never one line per answer.
    month_symptoms: {
      // v2–v6: "Har du haft besvär som kan vara relevanta för behandlingen?"
      label: "Besvär som kan vara relevanta för behandlingen",
      ...NEG,
      // v7 asks SYMPTOM_CHANGE_GATE; the label is Ö-734's.
      asRegisterQuestion: {
        questionId: "SYMPTOM_CHANGE_GATE",
        label:
          "Nya, pågående eller förändrade besvär sedan senaste incheckningen",
      },
    },
    month_symptom_types: { label: "Besvär" },
    month_intake: { label: "Kunnat äta och dricka tillräckligt" },
    month_symptom_care: { label: "Sökt vård för besvären" },
    // v7 (TES-1251)
    ...perSymptomPhrasing("month"),
  },
};

export const JOURNAL_FIELD_PHRASING: Readonly<
  Record<string, TemplatePhrasing>
> = {
  intake: INTAKE,
  prescription_transfer: TRANSFER,
  f01: F01,
  weekly_checkin: WEEKLY,
  milestone: MILESTONE,
};

/**
 * The entry for a field of the version she answered, or null: then her
 * answer is not written in the box (it stays behind "Källa"). An unknown
 * version (a caller without it) reads the field key as it stands.
 * `askedAs` is the question as that version asked it (the summary item's
 * label); it picks the label of a field later reworded to a register
 * question (`asRegisterQuestion`). Without it, the field's original label.
 */
export function journalFieldPhrase(
  templateKey: string,
  fieldKey: string,
  templateVersion?: number | null,
  askedAs?: string | null,
): JournalFieldPhrase | null {
  const t = JOURNAL_FIELD_PHRASING[templateKey];
  if (!t) return null;
  if (templateVersion != null && templateVersion < t.fromVersion) return null;
  const phrase = t.fields[fieldKey];
  if (!phrase) return null;
  const reworded = phrase.asRegisterQuestion;
  if (
    reworded &&
    askedAs &&
    asksRegisterQuestion(reworded.questionId, askedAs)
  ) {
    return { ...phrase, label: reworded.label };
  }
  return phrase;
}

// The question she was asked is the register question's copy, in either
// language the summary was built in.
function asksRegisterQuestion(
  questionId: QuestionId,
  askedAs: string,
): boolean {
  const copy = registerPromptFor(questionId)?.copy;
  if (!copy) return false;
  const asked = askedAs.trim();
  return asked === copy.sv.trim() || asked === copy.en.trim();
}

// The stored values that say "no" / "none": the option values of the forms'
// negative answers, and a false checkbox.
const NEGATIVE_VALUES: ReadonlySet<string> = new Set([
  "nej",
  "inget",
  "inga",
  "ingen",
  "inget_av_dessa",
  "none_of_these",
]);

/** Her stored answer is a plain "no" (or "none of these"). */
export function isNegativeAnswer(value: unknown): boolean {
  if (value === false) return true;
  if (typeof value === "string") return NEGATIVE_VALUES.has(value);
  if (Array.isArray(value)) {
    return (
      value.length > 0 &&
      value.every((v) => typeof v === "string" && NEGATIVE_VALUES.has(v))
    );
  }
  return false;
}

/** A bare number gets its unit: F-01 stores "82", the forms render "82 kg". */
export function journalMeasureValue(value: string, unit: string): string {
  const v = value.trim();
  return /^\d+([.,]\d+)?$/.test(v) ? `${v} ${unit}` : v;
}

/** "{label}: {value}", the one shape every phrased answer takes. */
export function journalPhrasedLine(label: string, value: string): string {
  return `${label}: ${value}`;
}
